import React, { useState } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { BugReportModal } from '@/components/BugReportModal';

interface ErrorFallbackProps {
  error: Error;
  resetErrorBoundary?: () => void;
  errorInfo?: React.ErrorInfo;
}

export function ErrorFallback({ error, resetErrorBoundary, errorInfo }: ErrorFallbackProps) {
  const [showBugReport, setShowBugReport] = useState(false);

  const handleReload = () => {
    if (resetErrorBoundary) {
      resetErrorBoundary();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleReportBug = () => {
    setShowBugReport(true);
  };

  return (
    <>
      <div className="min-h-screen bg-app-surface flex items-center justify-center p-4">
        <Card className="w-full max-w-lg md:max-w-xl">
          <CardContent className="p-8 md:p-10 lg:p-12 text-center space-y-6">
            {/* Icon */}
            <div className="mx-auto w-20 h-20 md:w-24 md:h-24 rounded-full bg-app-error-bg flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 md:h-12 md:w-12 text-app-error-text" />
            </div>

            {/* Title and Description */}
            <div className="space-y-3">
              <h1 className="text-2xl md:text-3xl font-bold text-app-text">
                ⚠️ Hov, der skete en fejl
              </h1>
              <p className="text-sm md:text-base text-app-text-secondary max-w-md mx-auto">
                Vi beklager ulejligheden. Fejlen er automatisk blevet rapporteret til vores tekniske team.
              </p>
            </div>

            {/* Error Message (Collapsible) */}
            {error.message && (
              <details className="text-left">
                <summary className="text-xs text-app-text-tertiary cursor-pointer hover:text-app-text-secondary mb-2">
                  Vis tekniske detaljer
                </summary>
                <div className="mt-2 rounded-lg bg-app-secondary border border-app-border p-3">
                  <p className="text-xs font-mono text-app-text-secondary break-all">
                    {error.message}
                  </p>
                  {errorInfo?.componentStack && (
                    <pre className="mt-2 text-xs font-mono text-app-text-tertiary overflow-auto max-h-32">
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleReload}
                className="flex-1 gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Prøv igen
              </Button>
              
              <Button
                variant="outline"
                onClick={handleReportBug}
                className="flex-1 gap-2"
              >
                <Bug className="h-4 w-4" />
                Rapportér Fejl
              </Button>
            </div>

            {/* Secondary Action */}
            <Button
              variant="ghost"
              onClick={handleGoHome}
              className="w-full gap-2"
            >
              <Home className="h-4 w-4" />
              Gå til Forsiden
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Bug Report Modal — uses the Supabase-backed component (components/BugReportModal.tsx). */}
      <BugReportModal
        isOpen={showBugReport}
        onClose={() => setShowBugReport(false)}
      />
    </>
  );
}
